import { styled } from "goober";
import type { SupplierGap } from "../lib/spaceMemberSupplierGap";
import { makeUrl, routes } from "../lib/urls";
import { Button } from "./Button";
import { ConnectorLogo } from "./ConnectorLogo";
import { Icon } from "./Icon";
import { Modal } from "./Modal";
import { PreviewListBox } from "./PreviewList";
import { Text } from "./Text";

const Content = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 320px;
`;

const Head = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Footer = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const RowLink = styled("a")`
  all: unset;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  min-width: 0;

  &:hover {
    background: var(--alpha4);
  }

  & .arrow-icon {
    opacity: 0;
    flex-shrink: 0;
    display: flex;
    margin-left: auto;
  }

  &:hover .arrow-icon {
    opacity: 0.5;
  }
`;

const RowStatic = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  min-width: 0;
`;

const Badge = styled("div")`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BlockedText = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const Hl = styled("span")`
  color: var(--text-primary);
`;

type Props = {
  open: boolean;
  onClose: () => void;
  spaceName: string;
  gaps: readonly SupplierGap[];
  userNames: ReadonlyMap<string, string>;
  groupNames: ReadonlyMap<string, string>;
  busy?: boolean;
  onConfirm: () => void;
};

function sharerLabel(
  gap: SupplierGap,
  userNames: ReadonlyMap<string, string>,
  groupNames: ReadonlyMap<string, string>
): string {
  const names = [
    ...gap.sharerUserIds.map((id) => userNames.get(id)),
    ...gap.sharerGroupIds.map((id) => groupNames.get(id)),
  ].filter((n): n is string => Boolean(n));
  if (names.length === 0) return "Ask a tenant admin to share this";
  return `Ask ${names.slice(0, 2).join(", ")} or a tenant admin to share this`;
}

// The members being added, derived from the grants each gap would create.
function memberLabel(
  gaps: readonly SupplierGap[],
  userNames: ReadonlyMap<string, string>,
  groupNames: ReadonlyMap<string, string>
): { label: string; singular: boolean } {
  const names = new Map<string, string>();
  for (const gap of gaps) {
    for (const a of gap.additions) {
      if (a.userId)
        names.set(`u:${a.userId}`, userNames.get(a.userId) ?? "a member");
      else if (a.userGroupId)
        names.set(
          `g:${a.userGroupId}`,
          groupNames.get(a.userGroupId) ?? "a group"
        );
      else if (a.serviceAccountId)
        names.set(`s:${a.serviceAccountId}`, "a service account");
    }
  }
  const list = [...names.values()];
  if (list.length === 1) return { label: list[0]!, singular: true };
  return { label: `${list.length} members`, singular: false };
}

function leading(gap: SupplierGap) {
  return gap.scopeKind === "connected_app" ? (
    <ConnectorLogo
      template={{ name: gap.supplierName, identifier: gap.identifier ?? "" }}
      size={16}
    />
  ) : (
    <Icon name="skills" size={16} color="var(--text-primary)" />
  );
}

export function GrantSpaceAccessModal({
  open,
  onClose,
  spaceName,
  gaps,
  userNames,
  groupNames,
  busy,
  onConfirm,
}: Props) {
  const connectors = gaps.filter((g) => g.scopeKind === "connected_app").length;
  const skills = gaps.filter((g) => g.scopeKind === "skill").length;
  const grantable = gaps.filter((g) => g.canShare).length;
  const allGrantable = grantable === gaps.length;
  const member = memberLabel(gaps, userNames, groupNames);

  const connectorPhrase = `${connectors} ${connectors > 1 ? "connectors" : "connector"}`;
  const skillPhrase = `${skills} ${skills > 1 ? "skills" : "skill"}`;

  return (
    <Modal open={open} onClose={onClose} alert>
      <Content>
        <Head>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Grant access to {spaceName}
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            <Hl>{member.label}</Hl> {member.singular ? "needs" : "need"} access
            to {connectors > 0 && <Hl>{connectorPhrase}</Hl>}
            {connectors > 0 && skills > 0 ? " and " : ""}
            {skills > 0 && <Hl>{skillPhrase}</Hl>} used in {spaceName}.{" "}
            {allGrantable
              ? "Grant it to finish adding them."
              : "Grant what you can now — the rest need an owner who can share them."}
          </Text>
        </Head>
        <PreviewListBox>
          {gaps.map((gap) =>
            gap.canShare ? (
              <RowLink
                key={gap.supplierId}
                href={makeUrl(
                  gap.scopeKind === "connected_app"
                    ? routes.modal.connector
                    : routes.modal.skill,
                  { id: gap.supplierId }
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Badge>{leading(gap)}</Badge>
                <Text
                  variant="text-regular"
                  color="var(--text-primary)"
                  ellipsis
                  style={{ flex: 1, minWidth: 0 }}
                >
                  {gap.supplierName}
                </Text>
                <span className="arrow-icon">
                  <Icon name="arrow-up-right" size={12} />
                </span>
              </RowLink>
            ) : (
              <RowStatic key={gap.supplierId}>
                <Badge style={{ opacity: 0.5 }}>{leading(gap)}</Badge>
                <BlockedText>
                  <Text
                    variant="text-regular"
                    color="var(--text-primary)"
                    ellipsis
                  >
                    {gap.supplierName}
                  </Text>
                  <Text
                    variant="text-small"
                    color="var(--text-secondary)"
                    ellipsis
                  >
                    {sharerLabel(gap, userNames, groupNames)}
                  </Text>
                </BlockedText>
                <Icon name="lock" size={16} color="var(--text-secondary)" />
              </RowStatic>
            )
          )}
        </PreviewListBox>
        <Footer>
          <Button size="l" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="l"
            onClick={onConfirm}
            disabled={busy || grantable === 0}
          >
            {allGrantable ? "Grant and add" : `Grant the ${grantable} I can`}
          </Button>
        </Footer>
      </Content>
    </Modal>
  );
}
